import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import {
  ThumbsUp, CircleSlash, User, Briefcase, DoorOpen, ShieldCheck, Home, Car,
  BadgeDollarSign, HelpCircle, Sparkles, Zap, Flame, Crown, BadgeCheck, Wallet,
  Users, TrendingUp, GraduationCap, ArrowLeft, ArrowRight, PartyPopper, CheckCircle2, XCircle,
} from "lucide-react";
import { QUIZ_QUESTION, QUIZ_OPTIONS, QUIZ_DISQUALIFIER } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  thumbsUp: ThumbsUp, circleSlash: CircleSlash, user: User, briefcase: Briefcase,
  doorOpen: DoorOpen, shieldCheck: ShieldCheck, home: Home, car: Car,
  badgeDollarSign: BadgeDollarSign, helpCircle: HelpCircle, sparkles: Sparkles,
  zap: Zap, flame: Flame, crown: Crown, badgeCheck: BadgeCheck, wallet: Wallet,
  users: Users, trendingUp: TrendingUp, graduationCap: GraduationCap,
};

const slide = {
  enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

function OptionCard({ option, index, selected, disabled, onSelect }) {
  const Icon = ICONS[option.icon] || Sparkles;
  return (
    <motion.button
      data-testid={`quiz-option-${index}`}
      onClick={onSelect}
      disabled={disabled}
      type="button"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.04 + index * 0.04, duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className={`quiz-card-option ${selected ? "selected" : ""} w-full min-h-[60px] sm:min-h-[68px] flex items-center gap-3 sm:gap-4 rounded-2xl border border-slate-700/80 bg-[#0b1528]/85 px-3.5 sm:px-5 py-3 sm:py-5 text-left`}
    >
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors duration-200 ${selected ? "bg-[#00C6EF]/20 border-[#00C6EF]/60 text-[#00C6EF]" : "bg-slate-800/70 border-slate-700 text-sky-300"}`}>
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1 text-[15px] sm:text-base font-bold text-white leading-snug [text-wrap:pretty] break-words">{option.label}</span>
      <span className={`h-5 w-5 shrink-0 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${selected ? "border-[#00C6EF] bg-[#00C6EF]" : "border-slate-600"}`}>
        {selected && <CheckCircle2 className="h-3.5 w-3.5 text-[#030712]" />}
      </span>
    </motion.button>
  );
}

/** Two columns only when every label is short enough to sit on one line on a phone. */
const compactGrid = (options) => options.every((o) => o.label.length <= 14);

export default function QuizPage() {
  const [path, setPath] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const licensedPath = path[0] === "Yes";
  const branchSteps = licensedPath
    ? [
        { key: "agentType", type: "choice", question: QUIZ_QUESTION.agentType, options: QUIZ_OPTIONS.agentType },
        { key: "whyNew", type: "choice", question: QUIZ_QUESTION.whyNew, options: QUIZ_OPTIONS.whyNew },
      ]
    : [
        { key: "salesExp", type: "choice", question: QUIZ_QUESTION.salesExp, options: QUIZ_OPTIONS.salesExp },
      ];
  const steps = [
    { key: "licensed", type: "choice", question: QUIZ_QUESTION.licensed, options: QUIZ_OPTIONS.licensed },
    ...branchSteps,
    { key: "age", type: "choice", question: QUIZ_QUESTION.age, options: QUIZ_OPTIONS.age },
    { key: "commission", type: "choice", question: QUIZ_QUESTION.commission, options: QUIZ_OPTIONS.commission, highlight: "Commission-Only" },
    { key: "motivation", type: "choice", question: QUIZ_QUESTION.motivation, options: QUIZ_OPTIONS.motivation },
    { key: "qualified", type: "lead" },
  ];
  const commissionIndex = steps.findIndex((s) => s.key === "commission");
  const disqualified = path[commissionIndex] === QUIZ_DISQUALIFIER;
  const currentIndex = submitted ? steps.length : Math.min(path.length, steps.length - 1);
  const step = submitted ? { key: "done", type: "done" } : disqualified ? { key: "unqualified", type: "unqualified" } : steps[currentIndex];
  const progress = Math.round(((currentIndex + 1) / 8) * 100);

  const record = (question, answer) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[currentIndex] = { question, answer };
      return next;
    });
  };

  const choose = (option) => {
    setSelected(option.label);
    record(step.question, option.label);
    setTimeout(() => {
      setDir(1);
      setPath((p) => [...p, option.label]);
      setSelected(null);
    }, 260);
  };

  const back = () => {
    if (currentIndex === 0) {
      window.location.href = "/";
      return;
    }
    setDir(-1);
    setPath((p) => p.slice(0, -1));
    setAnswers((a) => a.slice(0, -1));
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setError("");
    if (form.full_name.trim().length < 2 || !form.email.includes("@") || form.phone.trim().length < 7) {
      setError("Please make sure all information submitted is accurate");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads`, { ...form, answers });
      if (data.booking_token) {
        window.location.assign(`/apply/schedule?s=${encodeURIComponent(data.booking_token)}`);
        return;
      }
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong submitting your info. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] noise-overlay relative flex flex-col overflow-x-hidden" data-testid="quiz-page">
      <div className="absolute inset-0 hero-grid-bg" aria-hidden="true" />
      <div
        className="absolute -top-32 left-1/2 h-[420px] w-[760px] -translate-x-1/2 rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(closest-side, #00C6EF, transparent)" }}
        aria-hidden="true"
      />
      <header className="relative z-10 border-b border-slate-800/70 bg-[#030712]/70 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-5 py-1.5 sm:py-2 grid grid-cols-[1fr_auto_1fr] items-center">
          <div />
          <a href="/" data-testid="quiz-home-link" className="flex items-center justify-center">
            <img src="/bfg-logo.png" alt="Breeze Financial Group" className="h-28 sm:h-32 -my-7 sm:-my-8 w-auto object-contain drop-shadow-[0_0_18px_rgba(0,198,239,0.4)]" data-testid="quiz-header-logo" />
          </a>
          <div className="flex justify-end">
            <span className="font-mono-num text-xs text-slate-400" data-testid="quiz-progress-label">{Math.min(progress, 100)}%</span>
          </div>
        </div>
        <div className="h-[3px] w-full bg-slate-800/70">
          <motion.div
            data-testid="quiz-progress-bar"
            className="h-full bg-gradient-to-r from-[#00C6EF] to-[#2563EB]"
            animate={{ width: `${Math.min(progress, 100)}%` }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-start justify-center px-4 sm:px-5 pt-10 sm:pt-16 pb-20">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step.key}
              custom={dir}
              variants={slide}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              {step.type === "choice" && (
                <div>
                  <h1 className="text-center text-[26px] sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white leading-tight [text-wrap:balance]" data-testid="quiz-question">
                    {step.highlight ? (
                      <>
                        {step.question.split(step.highlight)[0]}
                        <span className="breeze-text">{step.highlight}</span>
                        {step.question.split(step.highlight)[1]}
                      </>
                    ) : (
                      step.question
                    )}
                  </h1>
                  <div className={`mt-7 sm:mt-9 grid gap-3 sm:gap-4 sm:grid-cols-2 ${compactGrid(step.options) ? "grid-cols-2" : "grid-cols-1"}`} data-testid="quiz-options">
                    {step.options.map((o, i) => (
                      <OptionCard key={o.label} option={o} index={i} selected={selected === o.label} disabled={selected !== null} onSelect={() => choose(o)} />
                    ))}
                  </div>
                </div>
              )}

              {step.type === "unqualified" && (
                <div className="text-center" data-testid="quiz-unqualified">
                  <motion.div
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 14 }}
                    className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-rose-500/10 border border-rose-400/40 text-rose-400"
                  >
                    <XCircle className="h-10 w-10" />
                  </motion.div>
                  <p className="mt-6 text-xs font-semibold uppercase tracking-[0.22em] text-rose-300/90">Not the right fit</p>
                  <h1 className="mt-3 text-[26px] sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white leading-tight [text-wrap:balance]" data-testid="quiz-question">
                    This opportunity isn't for you — and that's okay.
                  </h1>
                  <p className="mt-4 text-sm sm:text-base text-slate-300 max-w-md mx-auto leading-relaxed">
                    Breeze is built for entrepreneurial-minded people who want uncapped, 100% commission-based income.
                    If a salary is what you're looking for, we're not the right place — and we'd rather be upfront than waste your time.
                  </p>
                  <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a href="/" data-testid="quiz-unqualified-home" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-slate-700 bg-[#0b1528]/85 px-8 py-3.5 text-base font-bold text-white hover:border-slate-500 transition-colors">
                      <ArrowLeft className="h-4 w-4" /> Back to homepage
                    </a>
                    <button type="button" onClick={back} data-testid="quiz-unqualified-retry" className="w-full sm:w-auto btn-primary-glow rounded-full px-8 py-3.5 text-base font-bold text-white inline-flex items-center justify-center gap-2">
                      I understand it's commission-only <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {step.type === "lead" && (
                <div className="text-center">
                  <motion.div
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 14 }}
                    className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-[#00C6EF]/15 border border-[#00C6EF]/40 text-[#00C6EF]"
                  >
                    <PartyPopper className="h-8 w-8" />
                  </motion.div>
                  <h1 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight text-white" data-testid="quiz-qualified-title">
                    Congratulations!
                  </h1>
                  <p className="mt-1 text-lg font-bold breeze-text">You're Qualified!</p>
                  <h2 className="mt-6 text-xl sm:text-2xl font-extrabold breeze-text">You're Exactly Who We Built This For</h2>
                  <p className="mt-4 text-sm text-slate-300 max-w-lg mx-auto leading-relaxed">
                    You're not just another agent.<br />
                    You're a <strong className="breeze-text">driven</strong> closer, <strong className="breeze-text">eager for more</strong>, and ready to thrive with a team that delivers.
                  </p>
                  <p className="mt-4 text-sm sm:text-base font-bold text-white max-w-lg mx-auto">
                    Our proven leads, scripts, systems, and training have helped agents clear six-figures with ease.
                  </p>
                  <p className="mt-8 text-base sm:text-lg font-extrabold breeze-text">
                    Put in your information below and we will reach out:
                  </p>
                  <form onSubmit={submitLead} className="mt-6 max-w-md mx-auto space-y-4 text-left" data-testid="quiz-lead-form">
                    <input
                      type="text"
                      placeholder="Your full name"
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      data-testid="quiz-input-name"
                      className="w-full rounded-2xl border border-slate-700/80 bg-[#0b1528]/85 px-5 py-4 text-base text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00C6EF] focus:ring-2 focus:ring-[#00C6EF]/25 transition-all"
                    />
                    <input
                      type="email"
                      placeholder="Your e-mail address"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="quiz-input-email"
                      className="w-full rounded-2xl border border-slate-700/80 bg-[#0b1528]/85 px-5 py-4 text-base text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00C6EF] focus:ring-2 focus:ring-[#00C6EF]/25 transition-all"
                    />
                    <div className="flex rounded-2xl border border-slate-700/80 bg-[#0b1528]/85 overflow-hidden focus-within:border-[#00C6EF] focus-within:ring-2 focus-within:ring-[#00C6EF]/25 transition-all">
                      <span className="flex items-center px-4 text-sm font-mono-num text-slate-400 border-r border-slate-700/80">+1</span>
                      <input
                        type="tel"
                        placeholder="Your phone number"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        data-testid="quiz-input-phone"
                        className="w-full px-4 py-4 text-base text-white placeholder:text-slate-500 bg-transparent focus:outline-none"
                      />
                    </div>
                    {error && <p data-testid="quiz-form-error" className="text-sm text-red-400 text-center">{error}</p>}
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="quiz-submit-button"
                      className="btn-primary-glow w-full rounded-full px-8 py-4 text-base font-bold text-white disabled:opacity-60"
                    >
                      {submitting ? "Submitting..." : "Submit Info"}
                    </button>
                    <p className="text-[11px] text-slate-500 text-center">*Please make sure all information submitted is accurate</p>
                  </form>
                </div>
              )}

              {step.type === "done" && (
                <div className="text-center" data-testid="quiz-success">
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 180, damping: 13 }}
                    className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-emerald-400/15 border border-emerald-400/40 text-emerald-400"
                  >
                    <CheckCircle2 className="h-10 w-10" />
                  </motion.div>
                  <h1 className="mt-6 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">You're In, {form.full_name.split(" ")[0]}.</h1>
                  <p className="mt-4 text-sm sm:text-base text-slate-300 max-w-md mx-auto leading-relaxed">
                    Your info has been received and routed to a Breeze team leader. Keep your phone close — we will reach
                    out shortly to talk next steps.
                  </p>
                  <p className="mt-6 text-xs font-semibold uppercase tracking-[0.22em] text-sky-400">
                    Welcome to the winning side.
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {step.type !== "done" && step.type !== "unqualified" && currentIndex > 0 && (
            <div className="mt-10 flex justify-center">
              <button
                onClick={back}
                data-testid="quiz-back-button"
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-sky-300 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
